// Silences the benign "ResizeObserver loop completed with undelivered
// notifications" (and the older "ResizeObserver loop limit exceeded") browser
// warning. It fires when a ResizeObserver callback schedules another layout
// change within the same frame — harmless, but Create React App's dev error
// overlay catches the window `error` event and renders a full-screen red box.
//
// We add capture-phase listeners *before* the overlay registers its own, then
// stopImmediatePropagation so the overlay never sees these specific events.
// Every other real error passes through untouched.

const RESIZE_OBSERVER_MESSAGES = [
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded',
];

function isResizeObserverError(message) {
  if (typeof message !== 'string') return false;
  return RESIZE_OBSERVER_MESSAGES.some((m) => message.includes(m));
}

export function silenceResizeObserverErrors() {
  if (typeof window === 'undefined') return;

  // 0. Primary fix — stop the error at its source.
  //
  // The window `error` listeners below CANNOT reliably beat webpack-dev-server's
  // overlay: it registers its own window listener before this app's entry runs,
  // and for an error dispatched AT `window` the DOM invokes listeners in
  // REGISTRATION ORDER (the capture flag is ignored at the target), so the
  // overlay fires first and stopImmediatePropagation comes too late.
  //
  // Instead we wrap ResizeObserver so the user callback runs on the next
  // animation frame. That moves the callback out of the observer's delivery
  // cycle, so the browser never detects the same-frame resize loop and the
  // "undelivered notifications" warning is never emitted. recharts/MUI create
  // their observers at render time (after this runs), so the patch is in place
  // before the first one exists.
  const RO = window.ResizeObserver;
  if (RO && !RO.__nexumaPatched) {
    const Patched = class extends RO {
      constructor(callback) {
        super((entries, observer) => {
          window.requestAnimationFrame(() => {
            if (typeof callback === 'function') callback(entries, observer);
          });
        });
      }
    };
    Patched.__nexumaPatched = true;
    window.ResizeObserver = Patched;
  }

  // 1. Stop the raw window `error` event from reaching the CRA dev overlay.
  window.addEventListener(
    'error',
    (event) => {
      if (isResizeObserverError(event.message)) {
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    },
    true, // capture phase — runs before the overlay's bubble-phase listener
  );

  // 2. Some setups surface it as an unhandled rejection instead.
  window.addEventListener(
    'unhandledrejection',
    (event) => {
      const reason = event && event.reason;
      const message = typeof reason === 'string' ? reason : reason && reason.message;
      if (isResizeObserverError(message)) {
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    },
    true,
  );
}
